import { Accordion, AccordionDetails, AccordionSummary, Stack, Box, IconButton, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VideoFileIcon from '@mui/icons-material/VideoFileOutlined';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { format, parseISO } from 'date-fns';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import { type ChangeEvent, useMemo, type MouseEvent, useState } from 'react';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto.ts';
import MediaAssetList from '@/components/media/organization-media-list/media-asset-list.tsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaUpload } from '@/hooks/use-media-upload.ts';
import { useMediaUploadQuotaModals } from '@/hooks/use-media-upload-quota-modals.ts';
import MediaUploadFeedback from '@/components/media/media-upload-feedback.tsx';
import InsufficientStorageContactModal from '@/components/events/detail/tabs/common/media/insufficient-storage-contact.modal.tsx';
import AddMediaStorageModal from '@/components/events/detail/tabs/common/media/add-media-storage.modal.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import { downloadFile } from '@/lib/download-file.ts';
import { useGetMediaDownloadUrlMutation } from '@/state/queries/media/use-get-media-download-url.ts';
import { useDeleteMediaMutation } from '@/state/mutations/media/use-delete-media.ts';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import UpdateMediaForm from '@/components/media/organization-media-list/update-media.form.tsx';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import { getMediaUploadErrorCode } from '@/helpers/media-upload-quota.helper.ts';
import { MediaProcessingInsightBadge } from '@/components/media/media-processing-insight-badge.tsx';
import { MediaIncompleteUploadNotice } from '@/components/media/media-incomplete-upload-notice.tsx';
import { getProcessingInsightForMediaItem } from '@/helpers/media-processing-insight.helper';
import { useMediaProcessingInstructionsContext } from '@/hooks/use-media-processing-instructions-context';
import { useMediaPermissions } from '@/hooks/use-media-permissions';

export default function MediaCard({ media }: { media: MediaDto }) {
  const t = useTranslations();
  const notification = useNotifications();
  const instructionsContext = useMediaProcessingInstructionsContext(media);
  const permissions = useMediaPermissions(media);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const mediaUpload = useMediaUpload();
  const quotaModals = useMediaUploadQuotaModals(undefined);
  const { mutateAsync: downloadMediaMutation, isPending: isDownloadPending } = useGetMediaDownloadUrlMutation();
  const { mutateAsync: deleteMediaMutation, isPending: isDeletingPending } = useDeleteMediaMutation();

  const listedAssets = useMemo(
    () => media.assets.filter((asset) => asset.uploadStatus !== 'uploading'),
    [media.assets],
  );
  const latestMediaAsset: MediaAssetDto | undefined = listedAssets[listedAssets.length - 1];

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const res = await mediaUpload.upload(media.id, file);
      notification.notifySuccess(t('media.notification.uploadSuccess', { name: res.name }));
      mediaUpload.reset();
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      if (!quotaModals.handleUploadError(error)) {
        const errorCode = getMediaUploadErrorCode(error);
        const errorMessage = t('media.notification.uploadError');
        const reason = errorCode ? `: ${t(`error.${errorCode}` as TranslationKey)}` : '';
        notification.notifyError(`${errorMessage}${reason}`);
      }
      mediaUpload.resetAfterUploadAttempt(error);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleFileDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      const { url } = await downloadMediaMutation(latestMediaAsset.uri);
      downloadFile(url);
    } catch {
      notification.notifyError(t('media.notification.downloadError'));
    }
  };

  const handleEditIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsEditing((prev) => !prev);
  };

  const handleDeleteIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteMedia = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await deleteMediaMutation({ mediaId: media.id });
      notification.notifySuccess(t('media.notification.deleteSuccess', { title: media.title }));
    } catch {
      notification.notifyError(t('media.notification.deleteError'));
    }
  };

  return (
    <Accordion key={media.id}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '.MuiAccordionSummary-content': {
            alignItems: 'center',
          },
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '50%', maxWidth: '500px', minWidth: '200px' }}>
            <VideoFileIcon fontSize="large" sx={{ mr: '1rem' }} />
            <Stack sx={{ width: '100%' }}>
              {isEditing ? (
                <UpdateMediaForm mediaId={media.id} title={media.title} onSuccess={() => setIsEditing(false)} />
              ) : (
                <Typography variant="h6">{media.title}</Typography>
              )}
              <Typography variant="body2">
                {latestMediaAsset
                  ? `${t('common.version')} ${latestMediaAsset.version}, ${format(parseISO(latestMediaAsset.createdAt), 'dd MMM yyyy')}`
                  : t('media.noMediaAssets')}
              </Typography>
              <MediaProcessingInsightBadge
                insight={getProcessingInsightForMediaItem(media)}
                instructionsContext={instructionsContext}
              />
              <MediaUploadFeedback mediaUpload={mediaUpload} />
              <MediaIncompleteUploadNotice media={media} hidden={mediaUpload.isBusy} />
            </Stack>
          </Box>
          <Grid container sx={{ mr: '1rem' }}>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <IconButton aria-label="edit" onClick={(e) => handleEditIconClick(e)}>
                {isEditing ? <CloseIcon /> : <EditIcon />}
              </IconButton>
              <IconButton
                aria-label="upload"
                component="label"
                loading={mediaUpload.isBusy}
                onClick={(e) => e.stopPropagation()}
                disabled={mediaUpload.isBusy}
              >
                <FileUploadIcon />
                <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
              </IconButton>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
              {permissions.canDownload ? (
                <IconButton
                  aria-label="download"
                  onClick={(e) => handleFileDownload(e)}
                  disabled={!latestMediaAsset}
                  loading={isDownloadPending}
                >
                  <FileDownloadIcon />
                </IconButton>
              ) : null}
              <IconButton aria-label="delete" onClick={(e) => handleDeleteIconClick(e)} loading={isDeletingPending}>
                <DeleteIcon />
              </IconButton>
              <ConfirmationModal
                open={isDeleteDialogOpen}
                title={t('media.deleteMediaModalTitle')}
                text={t('media.deleteMediaModalText', { title: media.title })}
                onConfirm={handleDeleteMedia}
                onCancel={handleDeleteModalCancel}
              />
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {media.description && <Typography variant="body2">{media.description}</Typography>}
        {listedAssets.length > 0 ? (
          <MediaAssetList media={media} mediaAssets={listedAssets} mediaId={media.id} mediaTitle={media.title} />
        ) : (
          <Typography variant="body2">{t('media.noMediaAssetsExtended')}</Typography>
        )}
      </AccordionDetails>
      <AddMediaStorageModal
        open={quotaModals.isAddStorageModalOpen}
        onModalClose={() => quotaModals.setIsAddStorageModalOpen(false)}
      />
      <InsufficientStorageContactModal
        open={quotaModals.isContactModalOpen}
        onModalClose={() => quotaModals.setIsContactModalOpen(false)}
      />
    </Accordion>
  );
}
